<template>
  <v-form
    ref="formRef"
    class="d-flex flex-column gr-8 user-form"
  >
    <template v-if="isLpu && 'postpredstav' in userData">
      <v-text-field
        v-model="userData.postpredstav"
        :label="t('Ваша должность', options)"
        :rules="validationRules.postpredstav"
        outlined
      />
      <v-text-field
        v-model="userData.surname"
        :label="t('Фамилия', options)"
        :rules="validationRules.surname"
        outlined
      />
      <v-text-field
        v-model="userData.first_name"
        :label="t('Имя', options)"
        :rules="validationRules.first_name"
        outlined
      />
      <v-text-field
        v-model="userData.second_name"
        :label="t('Отчество', options)"
        :rules="validationRules.second_name"
        outlined
      />
    </template>

    <v-text-field
      ref="emailFieldRef"
      v-model="userData.email"
      append-icon="ui-icon-at-sign"
      :label="t('Адрес электронной почты', options)"
      :rules="validationRules.email"
      :placeholder="emailPlaceholder"
      outlined
      :hide-details="isAlertForeignEmailVisible"
      @blur="checkEmail"
      @input="emit('reset-email-error')"
    />

    <v-expand-transition>
      <BaseAlert
        v-if="isAlertForeignEmailVisible"
        class="mt-2 mb-4"
        color="text-error"
      >
        {{ t('По закону можно использовать только почту российских компаний. Например:', options) }}
        <ul class="my-2">
          <li>Яндекс (@yandex.ru, @ya.ru)</li>
          <li>VK и Mail.ru (@vk.com, @mail.ru, @inbox.ru, @bk.ru, @list.ru, @internet.ru)</li>
          <li>Rambler (@rambler.ru, @ro.ru, @myrambler.ru)</li>
        </ul>
        {{ t('Если хотите использовать корпоративную почту, напишите в поддержку', options) }}
        <a
          href="mailto:help@medrocket.ru"
          class="primary--text link-decoration-underline cursor-pointer"
        >help@medrocket.ru</a>
      </BaseAlert>
    </v-expand-transition>

    <v-text-field
      v-model="userData.password"
      autocomplete="off"
      :label="t('Придумайте пароль', options)"
      :type="isPasswordVisible ? 'text' : 'password'"
      :append-icon="isPasswordVisible ? 'ui-icon-eye-open' : 'ui-icon-eye-close'"
      :rules="validationRules.password"
      :placeholder="passwordPlaceholder"
      outlined
      @click:append="isPasswordVisible = !isPasswordVisible"
    />
  </v-form>
</template>

<script lang="ts" setup>
import {
  computed, onMounted, reactive, ref, watch,
} from 'vue'
import I18NextVue from 'i18next'
import BaseAlert from 'www/vue_components/common/core/BaseAlert.vue'
import useFixFormAutofill from 'src/components/pages/profile/lpus/register/index/LpusRegisterContent/components/RegistrationForm/composables/useFixFormAutofill'
import type { UserFormData } from 'src/components/pages/profile/lpus/register/index/LpusRegisterContent/components/RegistrationForm/types'
import useValidation from 'src/components/pages/profile/lpus/register/index/LpusRegisterContent/components/RegistrationForm/composables/useValidation'
import useUserStorage from 'src/composables/useUserStorage'

const props = defineProps<{
  emailErrorMessage: string
  isAlertForeignEmailVisible: boolean
  isDoctor: boolean
  isLpu: boolean
}>()

type Emits = {
  (_event: 'reset-email-error'): void
}

const emit = defineEmits<Emits>()

const userData = reactive<UserFormData>(
  props.isDoctor
    ? {
      email: '',
      password: '',
    }
    : {
      postpredstav: '',
      surname: '',
      first_name: '',
      second_name: '',
      email: '',
      password: '',
    },
)

const { t, language } = I18NextVue
const options = { ns: language }

const isPasswordVisible = ref(false)

const {
  emailPlaceholder,
  passwordPlaceholder,
} = useFixFormAutofill()

const {
  getValidators,
  getPasswordValidation,
  getEmailValidation,
} = useValidation()

const { userStorage } = useUserStorage()

const validationRules = computed(() => {
  const { required, minLength, maxLength } = getValidators()
  const email = getEmailValidation()
  const password = getPasswordValidation()

  return {
    postpredstav: [required(t('Укажите должность', options)), maxLength(100)],
    surname: [required(t('Укажите фамилию', options)), maxLength(40)],
    first_name: [required(t('Укажите имя', options)), maxLength(40)],
    second_name: [required(t('Укажите отчество', options)), minLength(2), maxLength(40)],
    email: [
      email.required,
      email.maxLength,
      email.onlyEn,
      email.regex,
      props.emailErrorMessage || true,
      !props.isAlertForeignEmailVisible,
    ],
    password: [
      password.required,
      password.minLength,
      password.maxLength,
      password.onlyLettersAndDigits,
    ],
  }
})

const formRef = ref()
const emailFieldRef = ref()

async function checkEmail() {
  return emailFieldRef.value.validate()
}

async function validate() {
  return !!formRef.value.validate()
}

onMounted(() => {
  Object.assign(userData, userStorage.get('RegistrationFormUserForm') || userData)

  watch(userData, value => {
    userStorage.set('RegistrationFormUserForm', value)
  }, { deep: true })
})

defineExpose({
  validate,
  userData,
})
</script>

<style lang="scss" scoped>
.user-form :deep(.v-text-field .v-text-field__details) {
  margin-bottom: -20px;
}
</style>
